/**
 * Session view (S0): live roster + presence (from `evt.presence.changed`) and the chat log (from
 * `evt.chat.message`), with the input bar sending `cmd.chat.send` via the WsSession. Reads the
 * folded {@link SessionView} from the store; sends only through {@link AppApi}.
 */
import type {
  ConditionClauseView,
  ConditionView,
  DecisionView,
  FidelityChangedEvent,
  GameActionPayload,
  PolicyView,
  GameStatePayload,
  PresenceState,
  LiveStream,
  MilestoneChapter,
  RetroDocument,
  SessionView,
} from '../../core/index.ts';
import type { TermChrome, TermLine } from '../term/index.ts';
import type { AppApi } from '../shell/appApi.ts';
import { t } from '../i18n.ts';
import { complete, complete_ as c } from '../../core/index.ts';
import type { CommandSpec, Completion } from '../../core/index.ts';
import type { MessageKey } from '../i18n.ts';

const PRESENCE_MARK: Record<PresenceState, string> = {
  online: '●',
  away: '◐',
  offline: '○',
};

function short(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

/**
 * What to call a player: their name if they gave one, their short id if they did not.
 *
 * Ids are for the log and for bug reports; a room where everyone is `019fc6ec` is a room nobody can
 * talk about out loud - "019fc6ec has the pager" is not a sentence anyone says.
 */
function nameOf(view: SessionView | undefined, playerId: string): string {
  // The two sources spell the same player differently: the roster carries the full id off
  // `evt.session.memberJoined`, the projection shortens it on `seats[].player`. So an exact match is
  // tried first and a prefix match second - but ONLY when the prefix picks exactly one person.
  //
  // The uniqueness check is the whole point. Ids are uuidv7, so two players who joined within the same
  // second share their first eight characters; a prefix match that took the first hit rendered one
  // player's seat under the other player's name, which is worse than the id it was fixing.
  const exact = view?.members.find((m) => m.playerId === playerId);
  const candidates = exact
    ? [exact]
    : (view?.members.filter((m) => m.playerId.startsWith(playerId)) ?? []);
  const named = candidates.length === 1 ? candidates[0]!.displayName : undefined;
  return named && named.length > 0 ? named : short(playerId);
}

/**
 * The session's panels (the first step toward the 7-view tui-layout): `all` stacks everything (the
 * default), while the others focus one panel so a full board does not crowd out the timeline. `me` is
 * the personal slice (§10 personal-vs-shared): your seat, your capacity, your check-in.
 *
 * `chat` is the odd one: chat normally rides the right-hand rail beside whatever panel is up
 * (tui-layout §6.5 as a rail rather than a tab, because chat is ambient - you read it while doing
 * something else). `/view chat` is for reading it back, and gives it the full width to do that in.
 */
export const SESSION_PANELS = [
  'all',
  'board',
  'feed',
  'decisions',
  'policies',
  'me',
  'chat',
  'guide',
] as const;
export type SessionPanel = (typeof SESSION_PANELS)[number];

let panel: SessionPanel = 'all';

/**
 * How much of each panel's source the player has already seen (session-chrome.md §3.2, step 4).
 *
 * A watermark rather than a boolean, because "new" is relative to what you last looked at: the `●`
 * says a slice has GROWN since you were there, and looking at it clears it. Module state beside
 * `panel` for the same reason `panel` is - it is a property of this client's session, not of the game,
 * and nothing on the wire should learn about it.
 */
const seen = new Map<SessionPanel, number>();

/** The size of the slice a panel renders, or null when the panel has no growing source. */
function sliceSize(view: SessionView | undefined, key: SessionPanel): number | null {
  switch (key) {
    case 'feed':
      return view?.feed.length ?? 0;
    case 'chat':
      return view?.chat.length ?? 0;
    case 'decisions':
      return view?.game?.decisions.length ?? 0;
    case 'policies':
      return view?.game?.policies.length ?? 0;
    default:
      // board, me, guide and all are views OF state rather than streams: nothing accumulates to miss.
      return null;
  }
}

/** The active panel (exported for tests / the shell's status line). */
export function sessionPanel(): SessionPanel {
  return panel;
}

export function setSessionPanel(next: SessionPanel): void {
  panel = next;
}

/**
 * The running version, and the newest the server knows about (web-client.md §7). Shown always rather
 * than only when an update exists: "am I on a stale build?" is the first question anyone asks about a
 * bug report, and "up to date" is an answer to it.
 */
export function versionReadout(app: AppApi): string {
  const running = app.clientVersion();
  const latest = app.latestVersion();

  if (latest === null) {
    // The manifest has not been read yet - offline, or the check has not run. Claiming "up to date"
    // here would be a guess.
    return t('version.unknown', { running });
  }

  return latest === running
    ? t('version.upToDate', { running })
    : t('version.behind', { running, latest });
}

export function renderSession(app: AppApi): TermLine[] {
  const lines: TermLine[] = [];
  const id = app.activeSessionId();
  const view: SessionView | undefined = id ? app.store.getSession(id) : undefined;
  const shows = (p: SessionPanel): boolean => panel === 'all' || panel === p;

  const title = view?.meta.name ?? (id ? short(id) : 'session');
  lines.push({ text: t('session.title', { name: title }), cls: 'title' });
  lines.push({
    text: t('session.status', {
      connection: app.connectionStatus(),
      player: nameOf(view, app.playerId() ?? '?'),
      panel,
      panels: SESSION_PANELS.join('|'),
      version: versionReadout(app),
    }),
    cls: 'dim',
  });
  // Warm start (web-client.md §3): the cached view is display-only until sys.welcome confirms it.
  if (app.isReadOnly()) {
    lines.push({ text: t('session.readOnly'), cls: 'sys' });
  }
  lines.push({ text: '' });

  if (shows('board')) {
    // Roster with live presence.
    lines.push({ text: t('board.roster'), cls: 'dim' });
    if (view && view.members.length) {
      for (const m of view.members) {
        const presence = view.presence[m.playerId] ?? 'offline';
        const mark = PRESENCE_MARK[presence];
        const you = m.playerId === app.playerId() ? ' (you)' : '';
        const role = m.roleKey ? ` <${m.roleKey}>` : '';
        const left = m.state === 'left' ? ' (left)' : '';
        lines.push({
          text: `  ${mark} ${nameOf(view, m.playerId)}${role}${you}${left}  [${presence}]`,
        });
      }
    } else {
      lines.push({ text: t('board.rosterEmpty'), cls: 'dim' });
    }
    lines.push({ text: '' });

    // Game board (the S1 engine bridge): the projected engine state, once the session runs a scenario.
    if (view?.game) {
      lines.push({ text: '' });
      for (const l of renderGameBoard(view.game, view)) lines.push(l);

      // A proposal only visible to whoever opened the policies panel is still a surprise adoption, so
      // the standing-rule someone wants the table to live under gets a line on the default screen.
      const proposed = view.policyPreviews ?? [];
      if (proposed.length) {
        lines.push({
          text: t('board.policyProposed', { names: proposed.map((c) => c.name).join(' · ') }),
          cls: 'sys',
        });
      }
    }
  }

  // The timeline (§4.2 / tui-layout §6.3): what everyone did and what the engine made of it, in one
  // stream.
  if (shows('feed')) {
    lines.push({ text: '' });
    for (const l of renderTimeline(view)) lines.push(l);
  }

  // Chat, given the whole width to be read back in. Everywhere else it is the rail (renderSessionSide).
  if (panel === 'chat') {
    lines.push({ text: '' });
    for (const l of renderChat(app, view)) lines.push(l);
  }

  // The decision queue (§6.5/§7.4): the open calls, who owns each, and what is still missing. On its own
  // panel it is the full docket; stacked in `all` the board already carries the one-line summary.
  if (panel === 'decisions') {
    lines.push({ text: '' });
    for (const l of renderDecisions(view)) lines.push(l);
  }

  // The policy ledger (§7.6): what governance actually changed, including what it undid.
  if (panel === 'policies') {
    lines.push({ text: '' });
    for (const l of renderPolicies(view)) lines.push(l);
  }

  // The seat guide (session-help.md §3.4): what each seat is FOR. On its own panel because it is a
  // reference, not a live surface - you read it once, or when someone new sits down.
  if (panel === 'guide') {
    lines.push({ text: '' });
    for (const l of renderGuide(view)) lines.push(l);
  }

  // The personal panel (§10): your own seat slice, separated from the shared board. Only on its own
  // panel — the stacked `all` view already carries the board's one-line `you:` summary.
  if (panel === 'me') {
    lines.push({ text: '' });
    for (const l of renderPersonal(app, view)) lines.push(l);
  }

  // Fidelity + spend (S2.6). Only shown once the ladder has actually moved: before that there is
  // nothing honest to say, and a permanent "full fidelity" badge would be decoration rather than news.
  if (view?.fidelity && shows('board')) {
    lines.push({ text: '' });
    for (const l of renderFidelity(view.fidelity)) lines.push(l);
  }

  // A stream arriving right now (protocol.md §8) - the retro being written, most often. Rendered above
  // the retro block because while it runs it IS the retro; the committed document replaces it on arrival.
  if (view?.stream && shows('board')) {
    lines.push({ text: '' });
    for (const l of renderStream(view.stream)) lines.push(l);
  }

  // Retro (the teaching moment): the engine-computed reflection, from session end or an on-demand /retro.
  if (view?.retro && shows('board')) {
    lines.push({ text: '' });
    for (const l of renderRetro(view.retro)) lines.push(l);
  }

  // The printed tail. Three lines was enough when every print was one line; contextual /help answers in
  // six or seven, and a window that truncates the answer to its last line is worse than no answer -
  // the player reads "full list: /commands" and never sees the verbs it was about (session-help.md §3.2).
  const log = app.log();
  if (log.length) {
    lines.push({ text: '' });
    for (const entry of log.slice(-LOG_TAIL_LINES)) lines.push({ text: entry, cls: 'log' });
  }
  return lines;
}

/** How much of the printed log the board shows. Sized to fit a whole `/help` answer, not one line. */
const LOG_TAIL_LINES = 12;

/** How many chat lines the rail carries. The rest are read back with `/view chat`. */
const CHAT_RAIL_LINES = 12;

/**
 * The chat log. `limit` caps the rail so a long conversation cannot outgrow the board beside it; the
 * focused `/view chat` panel passes none and shows the lot.
 */
function renderChat(app: AppApi, view: SessionView | undefined, limit?: number): TermLine[] {
  const lines: TermLine[] = [{ text: t('board.chat'), cls: 'dim' }];
  const all = view?.chat ?? [];

  // The outbox first, because "no messages yet" is false the moment you have said something - even if
  // the server has not confirmed it. A room that told you it was empty while your own line sat in it
  // unsent would be the exact confusion the two-stage render exists to remove.
  const waiting = outboxLines(app, view);
  if (!all.length) {
    lines.push(
      ...(waiting.length ? waiting : [{ text: t('board.chatEmpty'), cls: 'dim' as const }]),
    );
    return lines;
  }
  const shown = limit === undefined ? all : all.slice(-limit);
  // Say what was cut and where to find it, rather than silently showing a window as if it were all.
  if (shown.length < all.length) {
    lines.push({ text: t('board.chatEarlier', { n: all.length - shown.length }), cls: 'dim' });
  }
  // Answers to a mention (§3.5 tier 1) read in the scrollback beside the line they answer, so a
  // terminal player watching a workspace player talk to a character sees the exchange, not half of it.
  const answers = view?.npcChannelLines ?? [];

  for (const c of shown) {
    const mine = c.playerId === app.playerId();
    // A line aimed at YOUR seat carries a marker. The room still sees the mention - that is the point
    // of it being chat - but you get told it was pointed at you without a colour of its own.
    const at = mentionOf(c.text);
    const forMe = !!at && !!view?.game?.seats.some((s) => s.key === at.seat && s.mine);
    // THE ROOM, when it is not the default one (register item 16). Parity, in one line: this client
    // has no rooms and is not growing any - but it must not silently flatten a workspace player's
    // `#war-room` line into the same undifferentiated feed the bug produced. Absent means `#team`,
    // which is the whole scrollback here, so it stays unmarked.
    const room = c.channel && c.channel !== 'team' ? `#${c.channel} ` : '';
    lines.push({
      text: `${forMe ? '▸ ' : '  '}${room}${nameOf(view, c.playerId)}: ${c.text}`,
      cls: mine ? 'me' : 'peer',
    });

    for (const a of answers.filter((x) => x.forSeq === c.seq)) {
      const answerRoom = a.channel && a.channel !== 'team' ? `#${a.channel} ` : '';
      lines.push({ text: `  ${answerRoom}${a.seat}: ${a.text}`, cls: 'peer' });
    }
  }

  lines.push(...waiting);
  return lines;
}

/**
 * STAGE ONE: what you just said, before the server has said it back (the outbox).
 *
 * Always at the foot of the scrollback, because a pending line has no seq and therefore no place in
 * the sequence. A sending line is dimmed; a REFUSED one keeps its text and prints the two commands
 * that act on it, because a failure that deletes what you typed makes you reconstruct your own
 * sentence from memory (standards/messages.md: the useful half is the next move).
 */
function outboxLines(app: AppApi, view: SessionView | undefined): TermLine[] {
  const id = app.activeSessionId();
  if (!id || !view) return [];

  const committed = (seq: number) => view.chat.some((c) => c.seq === seq);
  return app
    .outbox()
    .linesIn(id, null, committed)
    .map((line) =>
      line.state === 'failed'
        ? {
            text: t('chat.failed', {
              text: line.text,
              error: line.error ?? '',
              id: short(line.cmdId),
            }),
            cls: 'err' as const,
          }
        : { text: t('chat.sending', { text: line.text }), cls: 'dim' as const },
    );
}

/**
 * The right-hand rail (tui-layout §6.5): chat, beside whatever panel is up. Chat is ambient - you read
 * it while doing something else - so it gets a column of its own rather than a turn in the main pane.
 *
 * Empty means one column: no session yet, or chat IS the panel, where a rail would be the same
 * conversation printed twice.
 */
export function renderSessionSide(app: AppApi): TermLine[] {
  if (panel === 'chat') return [];
  const id = app.activeSessionId();
  if (!id) return [];
  return renderChat(app, app.store.getSession(id), CHAT_RAIL_LINES);
}

/**
 * The timeline (tui-layout §6.3): the actions and the engine events they produced as ONE stream, in
 * the order they happened, rather than a list of actions with their consequences nested underneath.
 * Same records - but reconstructing a round means reading it forwards, and nesting made that a walk
 * through a tree.
 *
 * `●` is player-initiated, `○` is the world (engine events, narration, NPC speech). Marks rather than
 * colour, because the distinction has to survive a monochrome terminal (tui-layout §1).
 */
function renderTimeline(view: SessionView | undefined): TermLine[] {
  const lines: TermLine[] = [{ text: t('board.timeline'), cls: 'dim' }];
  const entries = view?.feed ?? [];
  if (!entries.length) {
    lines.push({ text: t('board.timelineEmpty'), cls: 'dim' });
    return lines;
  }
  // Newest last (seq order), capped so a long session does not push the board off-screen.
  const recent = entries.slice(-8);
  const width = Math.max(...recent.map((f) => String(f.seq).length));
  const stamp = (seq: number): string => `#${String(seq).padStart(width)}`;

  for (const f of recent) {
    lines.push({ text: `  ${stamp(f.seq)} ● ${f.actor ?? '?'} → ${f.kind}`, cls: 'sys' });

    // The scene read out loud (S2.4), ahead of everything else in the beat: this IS the standup opening,
    // and it is what a person at the table hears first. The board's templated digest stays where it is -
    // when no line arrives, that is what the team reads, so nothing here is load-bearing.
    if (f.scene) {
      const mark = f.scene.source === 'llm' ? '' : ' (scripted)';
      lines.push({
        text: `  ${stamp(f.seq)} ○ ${f.scene.scene}/${f.scene.beat}: ${f.scene.text}${mark}`,
        cls: 'you',
      });
    }

    // The narrated line comes first and reads as prose; the engine events follow as the record of what
    // actually happened. Prose never replaces the facts - a player who distrusts the narration can
    // still read the numbers that produced it.
    if (f.narration) {
      const mark = f.narration.source === 'llm' ? '' : ' (scripted)';
      lines.push({ text: `  ${stamp(f.seq)} ○ ${f.narration.text}${mark}`, cls: 'you' });
    }

    // What the NPCs said about this beat (S2.3), ahead of the engine record for the same reason the
    // narration is: people first, mechanism under them.
    for (const npc of f.npcLines) {
      const mark = npc.source === 'llm' ? '' : ' (scripted)';
      lines.push({ text: `  ${stamp(f.seq)} ○ ${npc.seat}: ${npc.text}${mark}`, cls: 'peer' });
    }

    for (const e of f.emitted) {
      const at = e.seat ? ` @${e.seat}` : '';
      const note = e.note ? `: ${e.note}` : '';
      lines.push({ text: `  ${stamp(f.seq)} ○ ${e.kind}${at}${note}`, cls: 'dim' });
    }
  }
  return lines;
}

/**
 * What each beat is for — the moves the engine will actually accept there (SceneRules, s1-game-core §4.4).
 * Beats the engine does not constrain still get a line, because the point of a guided scene is that the
 * team knows what this part of the meeting is supposed to produce.
 */
const BEAT_PROMPTS: Record<string, MessageKey> = {
  digest: 'beat.digest',
  reveals: 'beat.reveals',
  agenda: 'beat.agenda',
  commitments: 'beat.commitments',
  objective: 'beat.objective',
  keyResults: 'beat.keyResults',
  review: 'beat.review',
  docket: 'beat.docket',
  motions: 'beat.motions',
  deliberation: 'beat.deliberation',
  resolution: 'beat.resolution',
  close: 'beat.close',
};

/** The digest window, rendered as the standup's opening: counts, with the ugly parts called out. */
function digestLines(d: GameStatePayload['digest']): TermLine[] {
  if (d.quiet) {
    return [{ text: t('digest.quiet'), cls: 'dim' }];
  }

  const parts: string[] = [t('digest.rounds', { n: d.rounds })];
  if (d.events) parts.push(t('digest.events', { n: d.events }));
  if (d.decisions) parts.push(t('digest.decisions', { n: d.decisions }));
  if (d.workCompleted) parts.push(t('digest.work', { n: d.workCompleted }));
  if (d.hires) parts.push(t('digest.hires', { n: d.hires }));

  const lines: TermLine[] = [
    { text: t('digest.since', { round: d.sinceRound, parts: parts.join(' · ') }), cls: 'dim' },
  ];

  // The parts nobody volunteers at a standup.
  const bad: string[] = [];
  if (d.fuses) bad.push(t('digest.fuses', { n: d.fuses }));
  if (d.decisionsTimedOut) bad.push(t('digest.timedOut', { n: d.decisionsTimedOut }));
  if (d.concernsEscalated) bad.push(t('digest.escalated', { n: d.concernsEscalated }));
  if (d.promisesBroken) bad.push(t('digest.promises', { n: d.promisesBroken }));
  if (bad.length) lines.push({ text: t('digest.also', { parts: bad.join(' · ') }), cls: 'sys' });

  return lines;
}

/**
 * The decision-queue panel (§6.5/§7.4). A decision is not an event you react to: it has an owner, a
 * rights style saying who may close it, a clock, and a worst case that lands if nobody acts. The panel
 * shows exactly what is blocking each call so the team can see whose move it is.
 */
function renderDecisions(view: SessionView | undefined): TermLine[] {
  const lines: TermLine[] = [{ text: t('decisions.title'), cls: 'dim' }];
  const open = view?.game?.decisions ?? [];
  if (!open.length) {
    lines.push({ text: t('decisions.none'), cls: 'dim' });
    return lines;
  }

  for (const d of open) {
    const mine = d.mine ? ' (yours)' : '';
    const deferred = d.deferrals ? `, deferred ×${d.deferrals}` : '';
    lines.push({
      text: `  ${d.id} [${d.kind}] ${d.style} — owner ${d.owner}${mine}, due r${d.dueRound}${deferred}`,
      cls: d.mine ? 'sys' : undefined,
    });
    lines.push({
      text: t('decisions.options', { options: d.options.join(' | '), worst: d.worstCase }),
    });
    const blocking = decisionBlocker(d);
    if (blocking) lines.push({ text: t('decisions.needs', { blocker: blocking }), cls: 'dim' });
    lines.push({
      text: `    /decide ${d.id} ${d.options[0] ?? '<option>'} · /defer ${d.id}`,
      cls: 'dim',
    });
  }
  return lines;
}

/**
 * The policy panel (§7.6). A policy is a STANDING rule: its effects land every round while it is in force,
 * which is why the ledger shows status and not just a list — and why repealed rows stay visible.
 */
function renderPolicies(view: SessionView | undefined): TermLine[] {
  const lines: TermLine[] = [{ text: t('policies.title'), cls: 'dim' }];

  // Proposals first (S2.4 §4.2 step 4): a card the table is about to be asked to live under, with both
  // halves of it, and the command that adopts it. The draft used to reach one browser, which made the
  // next thing everyone else saw a rule they had never read.
  const previews = view?.policyPreviews ?? [];
  if (previews.length) {
    lines.push({ text: t('policies.proposed'), cls: 'dim' });
    for (const card of previews) {
      const effect = (e: { metric: string; value: number }): string =>
        `${e.metric} ${e.value > 0 ? '+' : ''}${e.value}/round`;
      lines.push({
        text: t('policies.proposedName', { name: card.name, id: card.id }),
        cls: 'sys',
      });
      lines.push({ text: t('policies.proposedProse', { prose: card.prose }), cls: 'dim' });
      lines.push({
        text: t('policies.proposedEffects', { effects: card.effects.map(effect).join(' · ') }),
        cls: 'dim',
      });
      lines.push({
        text: t('policies.proposedDownside', {
          effects: card.downsideEffects.map(effect).join(' · '),
        }),
        cls: 'dim',
      });
      lines.push({ text: t('policies.proposedAdopt', { id: card.id }), cls: 'dim' });
    }
  }

  const ledger = view?.game?.policies ?? [];
  if (!ledger.length) {
    if (!previews.length) lines.push({ text: t('policies.none'), cls: 'dim' });
    return lines;
  }

  for (const p of ledger as PolicyView[]) {
    const inForce = p.status !== 'Repealed';
    const on = p.amendment ? ` [${p.amendment}]` : '';
    lines.push({
      text: `  ${p.id}${on} — ${p.status.toLowerCase()}, adopted r${p.adoptedRound} by ${p.cause}`,
      cls: inForce ? 'sys' : 'dim',
    });
    if (!inForce) {
      lines.push({ text: t('policies.repealed', { round: p.changedRound }), cls: 'dim' });
      continue;
    }
    const variants = p.amendments.filter((a) => a !== p.amendment);
    if (variants.length) {
      lines.push({
        text: `    /amend ${p.id} ${variants[0]} (variants: ${variants.join(' | ')}) · /repeal ${p.id}`,
        cls: 'dim',
      });
    } else {
      lines.push({ text: `    /repeal ${p.id}`, cls: 'dim' });
    }
  }
  return lines;
}

/** What the rights style still requires before this call can be closed (§7.4), or null when it can. */
function decisionBlocker(d: DecisionView): string | null {
  switch (d.style) {
    case 'ConsultThenDecide':
      return d.consulted.length < d.requiredConsults
        ? t('decisions.blocker.consults', { n: d.requiredConsults - d.consulted.length, id: d.id })
        : null;
    case 'Committee':
      return d.votes < d.quorum
        ? t('decisions.blocker.votes', { n: d.quorum - d.votes, id: d.id })
        : null;
    case 'ApprovalChain':
      return d.approved
        ? null
        : t('decisions.blocker.approval', {
            approver: d.approver ?? t('decisions.approver'),
            id: d.id,
          });
    default:
      return null;
  }
}

/**
 * The seat guide (session-help.md §3.4): what each seat is FOR, grouped BY SEAT.
 *
 * Grouped that way on purpose - the game's whole shape is role asymmetry, so the reference has to teach
 * "you need each other" rather than "here are some verbs". Rendered entirely from the projection: no
 * authored copy to go stale when a scenario changes its seats.
 */
function renderGuide(view: SessionView | undefined): TermLine[] {
  const lines: TermLine[] = [{ text: t('guide.title'), cls: 'title' }];
  const game = view?.game;

  if (!game) {
    lines.push({ text: t('guide.noBoard'), cls: 'dim' });
    return lines;
  }

  for (const seat of game.seats) {
    // A bot seat is somebody: the scenario authored a character for it, and "(a bot plays this seat)"
    // threw that away in the one place a player is deciding who to talk to. When the content names
    // them, say the name and keep the fact that nobody human is there.
    const who = seat.mine
      ? t('guide.you')
      : seat.occupant === 'npc'
        ? seat.character
          ? t('guide.botNamed', { name: seat.character })
          : t('guide.bot')
        : seat.player
          ? `(${nameOf(view, seat.player)})`
          : seat.character
            ? t('guide.freeNamed', { name: seat.character })
            : t('guide.free');

    lines.push({ text: '' });
    lines.push({
      // DEC-16: the guide describes the team. It offers a role only while the room is still forming;
      // during play a seat nobody human holds is a colleague, and taking over is a deliberate move
      // rather than a prompt attached to every line about Daniel.
      text: `  ${seat.key}  ${who}${seat.claimable && !seat.mine && !game.started ? ` - /claim ${seat.key}` : ''}`,
      cls: seat.mine ? 'sys' : undefined,
    });

    if (seat.powers.length) {
      // Only your own seat's verbs are typeable; naming /do on someone else's would be a refusal.
      lines.push({
        text: seat.mine
          ? t('guide.verbs', { verbs: seat.powers.map((p) => `/do ${p}`).join(' · ') })
          : t('guide.verbsLocked', { verbs: seat.powers.join(', ') }),
        cls: 'dim',
      });
    }

    const owned = game.decisions.filter((d) => d.owner === seat.key);
    if (owned.length) {
      lines.push({
        text: t('guide.owns', {
          decisions: owned.map((d) => `${d.id} (${d.style}, r${d.dueRound})`).join(' · '),
        }),
        cls: 'dim',
      });
    }

    if (seat.mine) {
      lines.push({ text: t('guide.share', { seat: seat.key }), cls: 'dim' });
    } else if (seat.occupant === 'npc') {
      lines.push({ text: t('guide.ask', { seat: seat.key }), cls: 'dim' });
    }
  }

  // One paragraph of the loop, because the seats only make sense inside it.
  lines.push({ text: '' });
  lines.push({ text: t('guide.round'), cls: 'dim' });
  lines.push({ text: t('guide.round.checkin'), cls: 'dim' });
  lines.push({ text: t('guide.round.event'), cls: 'dim' });
  lines.push({ text: t('guide.round.decisions'), cls: 'dim' });
  lines.push({
    text: t('guide.round.scenes'),
    cls: 'dim',
  });
  lines.push({ text: '' });
  lines.push({ text: t('guide.footer'), cls: 'dim' });
  return lines;
}

/** The personal panel: the recipient's own seat slice from the per-seat projection. */
function renderPersonal(app: AppApi, view: SessionView | undefined): TermLine[] {
  const lines: TermLine[] = [{ text: t('me.title'), cls: 'dim' }];
  lines.push({ text: `  player ${nameOf(view, app.playerId() ?? '?')}` });
  // Private answers teammates have given YOU (S2.3). Personal panel only, for the same reason the
  // retro slice is: the board is shared and these are not.
  if (view?.npcReplies.length) {
    lines.push({ text: '' });
    lines.push({ text: t('me.quietWords'), cls: 'title' });
    for (const r of view.npcReplies.slice(-5)) {
      const mark = r.source === 'llm' ? '' : ' (scripted)';
      lines.push({ text: t('me.asked', { seat: r.seat, about: r.about }), cls: 'dim' });
      // Your own phrase, shown back before their answer (§2c). It is the honest half of a compile that
      // collapsed your sentence to a topic: you can see WHICH words it turned on, so the compression is
      // visible rather than hidden behind an answer that sounds like it understood everything.
      if (r.evidence) {
        lines.push({ text: t('me.heard', { quote: r.evidence }), cls: 'dim' });
      }
      lines.push({ text: `    ${r.text}${mark}`, cls: 'peer' });
    }
  }
  const you = view?.game?.you;
  if (!you) {
    // DEC-16: before the day starts this is the picker; after it, the same input is a TAKEOVER - you
    // are stepping into a chair somebody is already sitting in, and inheriting their character whole
    // (workspace §1b). Saying so is the difference between joining a team and shopping for a role.
    const started = view?.game?.started ?? false;
    lines.push({ text: t(started ? 'me.takeover' : 'me.noSeat'), cls: 'dim' });
    for (const s of view?.game?.seats.filter((seat) => seat.claimable) ?? []) {
      const title = s.title ?? s.key;
      const verbs = s.powers.join(', ') || t('me.noVerbs');
      lines.push({
        text: started
          ? `    /claim ${s.key} — ${t('me.takeoverFrom', { title, who: s.character ?? title })}`
          : `    /claim ${s.key} — ${title}: ${verbs}${s.npcEligible ? ' (a bot covers it otherwise)' : ''}`,
      });
    }
    return lines;
  }
  lines.push({
    text: t('me.seat', { seat: you.seat, capacity: you.freeCapacity, checkIn: you.checkIn }),
    cls: 'sys',
  });
  const mine = view?.game?.backlog.filter((b) => b.seat === you.seat) ?? [];
  for (const b of mine) {
    lines.push({ text: `    ${b.id} [${b.type}] ${b.status} ${b.progress}/${b.est}` });
  }

  // The private retro slice (S2.5). It belongs on the personal panel and nowhere else: it is written
  // from this seat's own state, and the board is shared.
  if (view?.personalRetro) {
    lines.push({ text: '' });
    lines.push({ text: t('retro.private'), cls: 'title' });
    if (view.personalRetro.source !== 'llm') {
      lines.push({ text: '  (composed from your own record)', cls: 'dim' });
    }
    for (const line of view.personalRetro.text.split('\n')) {
      lines.push({ text: line.length ? `  ${line}` : '', cls: 'you' });
    }
  }

  return lines;
}

/** The comparison symbols. Rendering is the client's call; the wire carries the operator, not the glyph. */
const OP_SYMBOL: Record<ConditionClauseView['op'], string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  neq: '≠',
};

/** The operator meaning the opposite – how a lose floor is read as the condition for staying alive. */
const OP_OPPOSITE: Record<ConditionClauseView['op'], ConditionClauseView['op']> = {
  gt: 'lte',
  gte: 'lt',
  lt: 'gte',
  lte: 'gt',
  eq: 'neq',
  neq: 'eq',
};

/**
 * One clause as a player reads it. A goal you are climbing towards renders as progress (`stability
 * 40/80`); anything else renders as the relation itself (`trust 54>0`), because "54/0" would be a lie
 * about which way the number has to move.
 *
 * `invert` flips a LOSE clause into its survival form: the board says what has to stay true, not what
 * would kill you – and a breached floor is marked rather than silently rendered as a false statement.
 */
function clauseText(c: ConditionClauseView, invert: boolean): string {
  const op = invert ? OP_OPPOSITE[c.op] : c.op;
  const current = c.current ?? c.currentText ?? '?';
  const target = c.target ?? c.targetText ?? '?';
  // Danger is "the losing side holds" for a lose clause, "not yet" is not danger for a win clause.
  const mark = invert ? (c.met ? ' ⚠' : '') : c.met ? ' ✓' : '';
  const body =
    c.target !== null && (op === 'gte' || op === 'lte')
      ? `${c.metric} ${current}/${target}`
      : `${c.metric} ${current}${OP_SYMBOL[op]}${target}`;
  return `${body}${mark}`;
}

/**
 * One condition as a line fragment. A win entry's clauses are ANDed, so they read as a list joined by
 * `·`. A lose entry is rendered in survival form, which by De Morgan turns its AND into an OR – so a
 * multi-clause floor is parenthesised and joined by "or" rather than pretending each half is fatal
 * alone.
 */
function conditionText(c: ConditionView, invert: boolean): string {
  const parts = c.clauses.map((cl) => clauseText(cl, invert));
  if (parts.length <= 1) return parts.join('');
  // `·` reads as "and" on this line, so only the alternatives inside an inverted floor need a word.
  const joined = parts.join(invert ? t('board.goalOr') : ' · ');
  return invert ? `(${joined})` : joined;
}

/**
 * The goal (first-run-review.md §A1). Without it every meter on the board is weather: the vision's
 * whole squeeze - trade this against that before the clock runs out - is unreadable when the targets
 * are only in the YAML.
 */
function goalLines(g: GameStatePayload): TermLine[] {
  const goal = g.goal;
  if (!goal) return [];
  const lines: TermLine[] = [];

  // Any win entry wins, so they are alternatives; the clauses inside one are all required.
  if (goal.win.length) {
    lines.push({
      text: t('board.win', {
        conditions: goal.win.map((w) => conditionText(w, false)).join(t('board.goalOr')),
      }),
      cls: 'sys',
    });
  }

  // THE ENDLESS FRAME SAYS SO OUT LOUD (DEC-17 M4). Without this line a sim board is a board with no
  // win and no deadline, which reads as a bug rather than as the game. The next gate is one line down,
  // and that is the goal here.
  if (goal.frame === 'sim') {
    lines.push({ text: t('board.endless'), cls: 'sys' });
  }

  // Every lose entry must be avoided, so their survival forms are all required: joined by `·`.
  if (goal.lose.length) {
    lines.push({
      // In an endless frame these are not how you lose - they are how the company ends.
      text: t(goal.frame === 'sim' ? 'board.collapse' : 'board.lose', {
        conditions: goal.lose.map((l) => conditionText(l, true)).join(' · '),
      }),
      // A breached floor changes colour: a fuse the team is standing on should not read as body text.
      cls: goal.lose.some((l) => l.clauses.some((c) => c.met)) ? 'err' : 'dim',
    });
  }

  if (goal.advance) {
    lines.push({
      text: t('board.advance', {
        // The gate's authored name, not its key (DEC-17 M1). "advance to renewal" was the board telling
        // a player an identifier; the deck has written "Renewal"/"Продление" since it was authored.
        phase: milestoneTitle(g, goal.advance.toPhase),
        conditions: conditionText(goal.advance.condition, false),
      }),
      cls: 'dim',
    });
  }

  return lines;
}

/** A phase's authored title in the reader's language, falling back to the key it is stored under. */
function milestoneTitle(g: GameStatePayload, name: string): string {
  const link = g.milestone?.chain.find((l) => l.name === name);
  return link?.title ?? name;
}

/**
 * The milestone chain as one line (DEC-17 M1): where the team has been, where they are, what is left.
 *
 * The chain is rendered rather than just the current link because a gate nobody can see coming is a
 * surprise, not a milestone - and the terminal has no calendar to put it on, so this line IS the
 * calendar. What the next gate opens rides along when the deck authors it, because "governance opens at
 * the next one" is the sentence that makes a locked ceremony legible instead of broken.
 */
function milestoneLines(g: GameStatePayload): TermLine[] {
  const m = g.milestone;
  if (!m || m.chain.length < 2) {
    // A one-phase deck has no chain to walk, and a line saying so is a line that costs a row and
    // teaches nothing.
    return [];
  }

  // A crossed gate says WHEN it was crossed (DEC-17 M1 increment 1). A chain that only marks where you
  // are is a progress bar; a chain that says "Respond: day 2" is a history, which is what the milestone
  // model is for. The day comes from the crossing log, so it is the round the engine recorded rather
  // than anything the client counted.
  const crossedOn = new Map(m.crossed.map((c) => [c.phase, c.round]));

  const lines: TermLine[] = [
    {
      text: t('board.milestone', {
        chain: m.chain
          .map((l) => {
            const name = l.title ?? l.name;
            if (l.current) return t('board.milestoneHere', { name });
            const day = crossedOn.get(l.name);
            return day === undefined ? name : t('board.milestoneCrossed', { name, day });
          })
          .join(' → '),
      }),
      cls: 'dim',
    },
  ];

  const next = m.chain[m.index + 1];
  if (next && next.unlocks.length > 0) {
    lines.push({
      text: t('board.milestoneUnlocks', {
        name: next.title ?? next.name,
        unlocks: next.unlocks.join(', '),
      }),
      cls: 'dim',
    });
  }

  return lines;
}

function renderGameBoard(g: GameStatePayload, view: SessionView | undefined): TermLine[] {
  const lines: TermLine[] = [];
  const status = g.started
    ? g.outcome !== 'None'
      ? g.outcome.toLowerCase()
      : 'in play'
    : 'not started';
  // The deadline belongs on the header line, not in a panel: the clock is the pressure the whole
  // scenario is built around, and a player who has to go looking for it is a player who forgets it
  // exists until it runs out. The server has always projected it; nothing rendered it.
  const clock = g.deadline === null ? '' : ` · r${g.round}/${g.deadline}`;
  lines.push({
    // The phase reads as the milestone it always was: the deck's own word for this stretch of the
    // company's life, not the enum value the scope indexes it by (DEC-17 M1).
    text: t('board.header', { round: g.round, clock, phase: milestoneTitle(g, g.phase), status }),
    cls: 'title',
  });

  // What company this is. One line, because the terminal is a dashboard and a dashboard has no brand -
  // but a player who cannot say which company they work at cannot read the fiction either (§2s).
  if (g.composition?.name) {
    lines.push({ text: t('board.company', { name: g.composition.name }), cls: 'dim' });
  }

  const meters = Object.entries(g.meters)
    .map(([k, v]) => `${k} ${v}`)
    .join('  ');
  if (meters) lines.push({ text: `  ${meters}`, cls: 'dim' });

  // The goal, immediately under the meters it gives meaning to: a target read three lines away from
  // its number is a target nobody connects to the number.
  for (const l of goalLines(g)) lines.push(l);

  // The chain under the gate condition that leaves it: "what it takes to advance" and "what we are
  // advancing through" are one thought, and a player who reads the first without the second is reading
  // a threshold with no story attached.
  for (const l of milestoneLines(g)) lines.push(l);

  // The outside world (§2a): who is waiting on you, and how close to the edge they are. The BAND is
  // what the wire carries - a raw patience number is the most mechanical number in the game and no
  // client should ever print it.
  for (const s of g.stakeholders ?? []) {
    const band =
      s.patienceBand === 'spent'
        ? t('board.stakeholderSpent')
        : s.patienceBand === 'impatient'
          ? t('board.stakeholderImpatient')
          : t('board.stakeholderSteady');
    lines.push({
      text: t('board.stakeholder', { who: s.name ?? s.key, band }),
      cls: s.patienceBand === 'spent' ? 'err' : 'dim',
    });
  }

  // What the team OWES (§7.5b). Under the stakeholder lines because a promise is a thing owed to one
  // of them, and above the calendar because it is the older debt. The engine has resolved these since
  // S1 and no client showed one, so nobody could name a promise to keep or break it.
  for (const p of g.promises ?? []) {
    lines.push({
      text: t(p.overdue ? 'board.promiseOverdue' : 'board.promise', {
        id: p.id,
        who: p.stakeholder,
        what: p.content,
        round: String(p.dueRound),
      }),
      cls: p.overdue ? 'err' : 'dim',
      command: `/kept ${p.id}`,
    });
  }

  // What the calendar owes next. The schedule always knew; nothing asked it, so a scheduled standup
  // arrived as a surprise every time. Suppressed by the projection while a scene is running, so this
  // line never contradicts the scene line below it.
  if (g.upcoming) {
    const { scene, inRounds } = g.upcoming;
    lines.push({
      text:
        inRounds === 0
          ? t('board.upcomingNow', { scene })
          : t('board.upcoming', { scene, rounds: inRounds }),
      cls: 'dim',
    });
  }

  // Situation digest: the "watch the fuse" gauges (§6.5/§9.3).
  const gg = g.gauges;
  const heat = gg.maxConcernHeat ? t('board.heat', { n: gg.maxConcernHeat }) : '';
  lines.push({
    text: t('board.situation', {
      risk: gg.telegraphedRisk,
      concerns: gg.openConcerns,
      heat,
      wip: gg.wip,
      reqs: gg.openReqs,
      decisions: gg.openDecisions,
      policies: gg.activePolicies,
    }),
    cls: 'dim',
  });

  // WHAT ARMS IT (DEC-17 M2). The risk gauge above is a number, and a conditional fuse cannot
  // contribute a countdown to it - so the trigger goes on its own line, through the same condition
  // readout the goal uses. "This tech debt explodes at launch" is the sentence the whole vocabulary
  // exists to make sayable; a gauge that showed only a total would keep it a secret.
  for (const fuse of g.gauges.armed ?? []) {
    lines.push({
      text: t('board.armed', {
        metric: fuse.metric,
        condition: fuse.condition ? conditionText(fuse.condition, false) : fuse.cause,
      }),
      // It reads as a warning the moment its condition is met and it is about to land.
      cls: fuse.condition?.met ? 'err' : 'dim',
    });
  }

  // Scene flow: the guided beat progress (§6.5), plus what this beat is for — a beat is a state the
  // engine enforces, so telling the team which moves belong here is the whole point of showing it.
  if (g.scene) {
    const s = g.scene;
    const shared = s.shared.length ? t('board.sceneShared', { n: s.shared.length }) : '';

    // A meeting the calendar opened has not started until somebody takes it, and saying which it is
    // matters: an offered standup blocks nothing, so a player who wants to keep working can (§A3).
    if (s.offered) {
      lines.push({ text: t('board.sceneOffered', { kind: s.kind }), cls: 'sys' });
    }

    lines.push({
      text: t('board.scene', {
        kind: s.kind,
        beat: s.beat,
        index: s.beatIndex + 1,
        count: s.beatCount,
        shared,
      }),
      cls: 'sys',
    });
    const prompt = BEAT_PROMPTS[s.beat];
    if (prompt) lines.push({ text: t('board.thisBeat', { prompt: t(prompt) }), cls: 'dim' });

    // The standup opens on the digest — read it here (§6.5 beat 1).
    if (s.beat === 'digest') {
      for (const l of digestLines(g.digest)) lines.push(l);
    }
  }

  // Seat picker: who holds each role, and which are yours / claimable. The powers ride on each free seat
  // because choosing a seat is choosing what you may do all session — role asymmetry is only a real choice
  // if it is legible before the choice is made.
  lines.push({ text: t('board.seats'), cls: 'dim' });
  for (const s of g.seats) {
    const who = s.mine
      ? '(you)'
      : // The person, not the id: `nameOf` shortens it itself when nobody gave a name, so this line
        // stays 80-column-safe now the projection carries the whole id.
        s.player
        ? `[${nameOf(view, s.player)}]`
        : s.occupant === 'npc'
          ? '(npc)'
          : '(free)';
    const check = s.checkedIn ? ' ✓' : '';
    // Your own seat names its verbs as commands you can type; other seats stay a list, because
    // /do on a seat you do not hold is a refusal waiting to happen (session-help.md §3.1).
    const powers = !s.powers.length
      ? ''
      : s.mine
        ? t('board.can', { verbs: s.powers.map((p) => `/do ${p}`).join(' · ') })
        : t('board.can', { verbs: s.powers.join(', ') });

    // A seat you can take is rendered as an affordance rather than prose (session-chrome.md §3.3):
    // the row carries the command, so it clicks, focuses and answers Enter - and the label shows the
    // typed form, so nothing here is pointer-only.
    //
    // DEC-16: only while the room is still forming, and only for a player who has no seat. Once the
    // day has started, a seat nobody human holds is a COLLEAGUE with a name, not an open chair with a
    // sign on it - offering [take this role] against Daniel mid-incident is the claiming ceremony
    // leaking into play. Taking over is still possible; it is just not what the board is for.
    // Only for a player who ALREADY holds a seat: that is DEC-13's lobby rearrangement, moving to a
    // different chair before the day starts. An unseated player gets the picker below instead, and
    // showing both was the same question asked twice on one screen.
    const claimable = s.claimable && !s.mine && !g.started && !!g.you;
    lines.push({
      text: `    ${s.key}: ${who}${check}${powers}${claimable ? `   ${t('board.claim')}` : ''}`,
      // Yours is reversed, free is actionable: one visual grammar, two meanings - "you are here"
      // and "you can act here".
      cls: s.mine ? 'seat-mine' : claimable ? 'seat-free' : undefined,
      ...(claimable ? { command: `/claim ${s.key}` } : {}),
    });
  }

  // The one thing a player who just arrived has to do. It sits on the board rather than in a panel they
  // would have to know to open: an unseated player is not playing, and a table that never fills is the
  // failure mode this line exists to prevent.
  if (!g.you) {
    // DEC-16: roles are CHOSEN, not claimed. The old line warned you that you had no seat and listed
    // keys - table-forming machinery (DEC-13) leaking into the first thing a new player reads. This
    // asks the question the player can actually answer, in the deck's own words, and leads with the
    // seat the scenario itself recommends.
    //
    // The claim INPUT is unchanged - DEC-16 is explicit that entry-time seat choice is the existing
    // input issued from a picker - so every row is still the typed command it always was.
    const open = g.seats.filter((s) => s.claimable);
    lines.push({ text: open.length ? t('board.whoAreYou') : t('board.takeoverOnly'), cls: 'me' });

    for (const s of [...open].sort((a, b) => Number(b.recommended) - Number(a.recommended))) {
      const title = s.title ?? s.key;
      lines.push({
        text:
          s.recommended && s.pitch
            ? t('board.pickRecommended', { title, pitch: s.pitch, seat: s.key })
            : s.pitch
              ? t('board.pickRolePitched', { title, pitch: s.pitch, seat: s.key })
              : t('board.pickRole', { title, seat: s.key }),
        cls: s.recommended ? 'seat-free' : undefined,
        command: `/claim ${s.key}`,
      });
    }
  } else if (!g.started) {
    // Only worth saying while it is still true: after the session starts, seats are fixed.
    lines.push({ text: '  (/claim <seat> to move · /release to give it up)', cls: 'dim' });
  }

  // Backlog / WIP (§2.8).
  if (g.backlog.length) {
    lines.push({ text: t('board.backlog'), cls: 'dim' });
    for (const b of g.backlog) {
      const at = b.seat ? ` @${b.seat}` : '';
      // The rate rides the line only when it is not 1 (DEC-17 M3). Printing "×1" on every item would
      // be noise on a default nobody chose; printing "×3" is the reason this item is eating the seat.
      const rate = (b.rate ?? 1) > 1 ? ` ×${b.rate}` : '';
      lines.push({
        text: `    ${b.id} [${b.type}] ${b.status} ${b.progress}/${b.est}${rate}${at}`,
      });
    }
  }

  // The director's pending event (§4.3): what happened, what only THIS seat can see, and the options
  // to resolve it with. The card id stays on the line because it is what the log and the retro call
  // this card - a player reporting a problem needs the same word the server used.
  if (g.activeEvent) {
    const ev = g.activeEvent;
    // The weight class rides on the line for the heavy tiers only. Marking a texture draw as "texture"
    // is noise; NOT marking a consequence is the board failing to say that this one was caused by
    // something the table already did (game-loop §9.1 class 1).
    const weight =
      ev.tier === 'consequence'
        ? t('board.eventConsequence')
        : ev.tier === 'scripted'
          ? t('board.eventScripted')
          : '';
    lines.push({ text: t('board.event', { id: ev.id }) + weight, cls: 'sys' });
    if (ev.text) {
      lines.push({ text: `    ${ev.text}` });
    }

    // The reveal is marked as YOURS on purpose. Everyone at the table is reading a screen; without a
    // marker a player cannot tell which lines the others can see, and the whole point of the mechanic
    // is knowing you hold something they do not.
    if (ev.reveal) {
      lines.push({ text: t('board.eventReveal', { reveal: ev.reveal }), cls: 'me' });
    }

    for (const o of ev.options) {
      // A bet's odds ride on its own option line: §9.7 says a gamble must be priced where it is taken,
      // not in a footnote somebody scrolls past.
      const odds = o.isBet ? t('board.bet', { odds: o.odds ?? t('board.oddsUnstated') }) : '';
      lines.push({
        text: t('board.eventOption', {
          id: o.id,
          label: o.label ?? t('board.eventUnlabelled'),
          odds,
        }),
        cls: 'dim',
      });
    }
  }

  // The decision queue, compactly (§6.5): the board says a call is open and whose it is; `/view decisions`
  // has the full docket with what each one still needs.
  if (g.decisions.length) {
    const summary = g.decisions
      .map((d) => `${d.id} (${d.mine ? t('board.decisionYours') : d.owner}, r${d.dueRound})`)
      .join(' · ');
    lines.push({ text: t('board.decisions', { summary }), cls: 'sys' });
  }

  // OKRs (§7.5): the objective + graded key results.
  if (g.okrs.objective || g.okrs.keyResults.length) {
    lines.push({ text: t('board.okrs'), cls: 'dim' });
    if (g.okrs.objective)
      lines.push({ text: t('board.objective', { objective: g.okrs.objective }) });
    for (const k of g.okrs.keyResults) {
      lines.push({ text: `    ${k.id}: ${k.description} — ${k.grade}` });
    }
  }

  if (g.you) {
    lines.push({
      text: t('board.you', {
        seat: g.you.seat,
        capacity: g.you.freeCapacity,
        checkIn: g.you.checkIn,
      }),
      cls: 'sys',
    });
  }
  return lines;
}

/**
 * The retro (S2.5): the story first, the numbers under it.
 *
 * The document arrives in two parts and this renders whichever parts exist. The package lands the moment
 * the session ends; the prose lands when the session-end job publishes it, which is a strong-tier call and
 * takes a few seconds. So a team sees its ending immediately and the story fills in, rather than staring at
 * a spinner or, worse, at nothing until the model answers.
 */
/**
 * What the session is paying, and what it has stopped paying for. The client computes none of this: the
 * rung, the spend and the list of what went quiet are all server-decided (tui-layout §11.1).
 */
function renderFidelity(f: FidelityChangedEvent): TermLine[] {
  const dollars = (micro: number): string => `$${(micro / 1_000_000).toFixed(2)}`;
  const lines: TermLine[] = [
    {
      text: t('fidelity.line', {
        rung: f.to.toLowerCase(),
        spent: dollars(f.spentMicro),
        ceiling: dollars(f.ceilingMicro),
        percent: f.percentUsed,
      }),
      cls: f.disabled.length ? 'err' : 'sys',
    },
  ];
  if (f.disabled.length) {
    lines.push({ text: t('fidelity.disabled', { purposes: f.disabled.join(', ') }), cls: 'dim' });
  } else {
    lines.push({ text: '  everything is running again', cls: 'dim' });
  }
  return lines;
}

/**
 * Model output as it arrives. The store releases text at sentence boundaries, so this renders whole
 * thoughts rather than characters; the cursor is what says more is coming.
 */
function renderStream(s: LiveStream): TermLine[] {
  const label = s.kind === 'llm.retro' ? 'retro' : 'writing';
  const lines: TermLine[] = [{ text: `${label} –`, cls: 'title' }];
  for (const line of s.text.split('\n')) {
    const heading = line.trim().length > 0 && line.trim() === line.trim().toLocaleUpperCase();
    lines.push({ text: line.length ? `  ${line}` : '', cls: heading ? 'sys' : 'you' });
  }

  if (!s.done) lines.push({ text: '  ▌', cls: 'dim' });
  return lines;
}

/**
 * The milestones this team has crossed (DEC-17 M1) - the session's chapters, oldest first.
 *
 * Rendered like the retro panel and deliberately NOT like the ending: no score, no verdict, no closing
 * observation, because the session is still live and a chapter that reads as a summing-up would tell a
 * team the game is over while they are still playing it. The engine's own headline stands until the
 * written note lands, so a dead provider costs the prose and never the chapter.
 */
function renderMilestones(chapters: readonly MilestoneChapter[]): TermLine[] {
  if (chapters.length === 0) {
    return [{ text: t('milestone.none'), cls: 'dim' }];
  }

  const lines: TermLine[] = [];
  for (const chapter of chapters) {
    lines.push({ text: t('milestone.header', { headline: chapter.headline }), cls: 'title' });

    if (chapter.text) {
      if (chapter.source !== 'llm') {
        lines.push({ text: t('milestone.scripted'), cls: 'dim' });
      }

      lines.push({ text: '' });
      for (const line of chapter.text.split('\n')) {
        // Section headings by SHAPE, not by a fixed list - they are localized, and the client is not the
        // place that decides what the sections are called. Same rule the retro panel follows.
        const heading = line.trim().length > 0 && line.trim() === line.trim().toLocaleUpperCase();
        lines.push({ text: line.length ? `  ${line}` : '', cls: heading ? 'sys' : 'you' });
      }
    } else {
      lines.push({ text: t('milestone.writing'), cls: 'dim' });
    }

    lines.push({ text: '' });
  }

  return lines;
}

function renderRetro(r: RetroDocument): TermLine[] {
  const p = r.package;
  const lines: TermLine[] = [{ text: t('retro.header', { headline: p.headline }), cls: 'title' }];

  if (r.text) {
    // Degrade honestly (meeting-llm §7), the same badge the narration feed carries: a player deserves to
    // know whether they are reading a model or the engine's own numbers.
    if (r.source !== 'llm') {
      lines.push({ text: '  (composed from the engine’s own record)', cls: 'dim' });
    }

    lines.push({ text: '' });
    for (const line of r.text.split('\n')) {
      // The section headings the retro is written in (WHAT HAPPENED / WHERE IT TURNED / THE ONE THING).
      // Recognised by shape rather than by a fixed list, because they are localized (EN/RU) and the
      // client is not the place that decides what the sections are called.
      const heading = line.trim().length > 0 && line.trim() === line.trim().toLocaleUpperCase();
      lines.push({ text: line.length ? `  ${line}` : '', cls: heading ? 'sys' : 'you' });
    }

    lines.push({ text: '' });
  } else if (p.ended) {
    lines.push({ text: '  writing the retro…', cls: 'dim' });
  }

  const grades = p.okrs.keyResults.map((k) => `${k.description}: ${k.grade}`).join('; ');
  // The survival breakdown (DEC-17 M4 §3), at a collapse. The terms, not the total: "we lasted 19 days
  // and reached 4 milestones" is a different sentence from "177", and it is the one a team can argue
  // about afterwards.
  if (p.survival) {
    const sv = p.survival;
    lines.push({
      text: t('retro.survival', {
        score: p.score ?? 0,
        final: sv.final,
        peak: sv.peak,
        peakWeight: sv.peakWeight,
        rounds: sv.rounds,
        roundWeight: sv.roundWeight,
        gates: sv.gates,
        gateWeight: sv.gateWeight,
      }),
      cls: 'sys',
    });
  }

  if (grades) lines.push({ text: t('retro.okrs', { grades }), cls: 'dim' });
  if (p.concerns.length) {
    const cs = p.concerns
      .map((c) => `${c.topic} (heat ${c.heatPeak}${c.escalated ? ', escalated' : ''})`)
      .join('; ');
    lines.push({ text: t('retro.concerns', { concerns: cs }), cls: 'dim' });
  }
  if (p.motifs.length) {
    lines.push({
      text: t('retro.motifs', {
        motifs: p.motifs.map((m) => `${m.label} ×${m.mentions}`).join(', '),
      }),
      cls: 'dim',
    });
  }
  return lines;
}

/**
 * Every command the session view understands, and how to use it. ONE table, because the help text and
 * the usage messages were previously a hand-maintained string literal that had already drifted from the
 * switch below - it listed commands that were gone and omitted several that worked.
 *
 * It also fixes a worse confusion: a known command used with the wrong arguments used to be reported as
 * "unknown command", because the input mapper returned null for both cases and the caller could not tell
 * them apart. Being told a command does not exist when it does is a dead end; being told how to use it
 * is not.
 */
export const SESSION_COMMANDS: ReadonlyMap<string, CommandSpec> = new Map<string, CommandSpec>([
  ['help', { usage: '/help - what you can do right now', args: [] }],
  ['commands', { usage: '/commands - the full command list, by object', args: [] }],
  ['start', { usage: '/start - begin the session', args: [] }],
  ['do', { usage: "/do <verb> [seat] - use one of your seat's powers", args: [c.verb, c.seat] }],
  ['claim', { usage: '/claim <seat> - take a seat', args: [c.freeSeat] }],
  ['release', { usage: '/release - give up your seat', args: [] }],
  ['checkin', { usage: '/checkin <seat> [mode] - check in (default Passed)', args: [c.seat] }],
  [
    'mood',
    { usage: '/mood <seat> <mood> - Happy|Satisfied|Stressed|Angry|Bored', args: [c.seat, c.mood] },
  ],
  ['scene', { usage: '/scene <kind> - open a scene', args: [c.scene] }],
  ['beat', { usage: '/beat - advance the scene', args: [] }],
  ['add', { usage: '/add <id> <type> <effort> - add a backlog item', args: [] }],
  ['work', { usage: '/work <id> <seat> - start work', args: [c.itemId, c.seat] }],
  [
    'allocate',
    {
      usage: '/allocate <id> <units> - put more capacity on an item (it goes faster)',
      args: [c.itemId],
    },
  ],
  ['pause', { usage: '/pause <id> - pause work', args: [c.itemId] }],
  ['abandon', { usage: '/abandon <id> - abandon work', args: [c.itemId] }],
  ['objective', { usage: '/objective <text> - set the objective', args: [] }],
  ['kr', { usage: '/kr <id> <expression> - add a key result', args: [c.none, c.metric] }],
  ['rmkr', { usage: '/rmkr <id> - remove a key result', args: [c.krId] }],
  ['okr', { usage: '/okr <goal> - turn a goal in your own words into key results', args: [] }],
  ['brief', { usage: '/brief - what this session is, and what winning looks like', args: [] }],
  ['policy', { usage: '/policy <agreement> - compile a working agreement into a rule', args: [] }],
  ['adopt', { usage: '/adopt <id> - adopt a policy you previewed', args: [c.none] }],
  [
    'share',
    { usage: '/share <seat> <item> - use your standup share slot', args: [c.seat, c.itemId] },
  ],
  // The promise surface (outside-world §3; npc-llm-layer §7.5b). `by <when>` is a ROUND, because the
  // engine's due date is one - a promise the player words in days and the engine reads in rounds would
  // be two clocks disagreeing about the same commitment.
  [
    'promise',
    {
      usage: '/promise <stakeholder> <what> by <round> - promise something to the outside world',
      args: [c.stakeholder],
    },
  ],
  ['kept', { usage: '/kept <id> - you delivered it', args: [c.promiseId] }],
  ['broke', { usage: '/broke <id> - you are not going to deliver it', args: [c.promiseId] }],
  ['concern', { usage: '/concern <seat> <topic> - raise a concern', args: [c.seat] }],
  ['resolveconcern', { usage: '/resolveconcern <id> - resolve a concern', args: [] }],
  ['raise', { usage: '/raise <id> - raise a decision', args: [c.decisionId] }],
  [
    'decide',
    { usage: '/decide <id> <option> - resolve a decision', args: [c.decisionId, c.decisionOption] },
  ],
  ['defer', { usage: '/defer <id> - defer a decision', args: [c.decisionId] }],
  [
    'consult',
    { usage: '/consult <id> <seat> - consult on a decision', args: [c.decisionId, c.seat] },
  ],
  [
    'vote',
    {
      usage: '/vote <id> <seat> <option> - vote on a decision',
      args: [c.decisionId, c.seat, c.decisionOption],
    },
  ],
  ['approve', { usage: '/approve <id> <seat> - approve a decision', args: [c.decisionId, c.seat] }],
  [
    'amend',
    {
      usage: '/amend <policy> <variant> <seat> - amend a policy',
      args: [c.policyId, c.policyVariant, c.seat],
    },
  ],
  ['repeal', { usage: '/repeal <policy> <seat> - repeal a policy', args: [c.policyId, c.seat] }],
  ['resolve', { usage: '/resolve [option] - resolve the pending event', args: [c.eventOption] }],
  [
    'ask',
    {
      usage:
        '/ask <seat> <mood|decision:id|concern:id> - ask a teammate, privately (only you see the reply)',
      args: [c.seat, c.askTopic],
    },
  ],
  ['view', { usage: `/view ${SESSION_PANELS.join('|')} - switch panel`, args: [c.panel] }],
  ['retro', { usage: '/retro - show the retro', args: [] }],
  // The outbox's two commands. Completed from the OUTBOX itself, which is the same rule the session
  // ids and the deck ids follow: what completes is what actually exists, never a set typed in here.
  [
    'retry',
    {
      usage: '/retry <id> - send a line that did not go out again',
      args: [(fold) => [...fold.pending]],
    },
  ],
  [
    'discard',
    {
      usage: '/discard <id> - drop a line that did not go out',
      args: [(fold) => [...fold.pending]],
    },
  ],
  ['milestones', { usage: '/milestones - read the milestones you have crossed', args: [] }],
  ['leave', { usage: '/leave - leave the session', args: [] }],
]);

/**
 * The catalog, grouped by the OBJECT a command acts on (session-help.md §3.4). The alphabet was never a
 * taxonomy: a player looking for "how do I close this decision" was reading a list sorted by a letter.
 *
 * Anything missing from a group still prints, under "other" - the point is to help someone scan, not to
 * hide a command because nobody assigned it a home.
 */
const COMMAND_GROUPS: readonly (readonly [MessageKey, readonly string[]])[] = [
  ['commands.group.seat', ['claim', 'release', 'do', 'checkin', 'mood', 'share']],
  ['commands.group.work', ['add', 'work', 'pause', 'abandon']],
  ['commands.group.okrs', ['objective', 'kr', 'rmkr']],
  [
    'commands.group.decisions',
    ['raise', 'decide', 'defer', 'consult', 'vote', 'approve', 'resolve'],
  ],
  ['commands.group.policy', ['concern', 'resolveconcern', 'amend', 'repeal']],
  ['commands.group.scenes', ['scene', 'beat']],
  ['commands.group.talking', ['ask']],
  ['commands.group.session', ['start', 'view', 'retro', 'help', 'commands', 'leave']],
];

/** The catalog, grouped, with the mention alongside the command that is its private twin. */
function commandCatalog(): string[] {
  const grouped = new Set(COMMAND_GROUPS.flatMap(([, names]) => names));
  const lines = [t('commands.header')];

  for (const [group, names] of COMMAND_GROUPS) {
    lines.push(`  ${t(group)}:`);
    for (const name of names) {
      const usage = SESSION_COMMANDS.get(name)?.usage;
      if (usage) lines.push(`    ${usage}`);
    }
    // The mention is not a command - it is chat - but it is the public half of the pair /ask belongs
    // to, and nobody finds it if the catalog only lists the slash side.
    if (group === 'commands.group.talking') lines.push(`    ${MENTION_USAGE}`);
  }

  const ungrouped = [...SESSION_COMMANDS.keys()].filter((k) => !grouped.has(k));
  if (ungrouped.length) {
    lines.push(`  ${t('commands.group.other')}:`);
    for (const name of ungrouped) lines.push(`    ${SESSION_COMMANDS.get(name)?.usage}`);
  }

  return lines;
}

export const MENTION_USAGE = '@<seat> <message> - say it to the room, aimed at a seat (public)';

/**
 * `@seat message` - a chat line addressed to a seat.
 *
 * It is deliberately NOT a private channel: `@` means "in front of the room" in every tool a player
 * arrives from, so it travels the ordinary chat path and everyone sees who was addressed. The private
 * 1-on-1 is `/ask`, which stays a command because its topic is a closed key the server validates -
 * a mention invites free text, and free text to an NPC is closed until the C2 validator exists.
 */
export function mentionOf(text: string): { seat: string; rest: string } | null {
  const m = /^@([\w-]+)\s*(.*)$/.exec(text.trim());
  return m ? { seat: m[1] ?? '', rest: (m[2] ?? '').trim() } : null;
}

/**
 * What a lost player is actually asking, in the words they type (session-help.md §3.3). A plea for help
 * is not chat: answering it in the room's face is the game being pedantic at the worst moment.
 *
 * The RU forms are here rather than behind i18n because this is the one surface where being understood
 * matters more than being consistent - a player typing "что делать" is already stuck.
 */
const HELP_PLEAS = new Set([
  'help',
  '?',
  'what can i do',
  'what can i do?',
  'what now',
  'what now?',
  'помощь',
  'что делать',
  'что делать?',
  'что я могу',
  'что я могу?',
]);

/**
 * Domain words that are not commands, and the command they mean (session-help.md §3.3). A player who
 * types `/standup` guessed the vocabulary right; "unknown command" tells them they know nothing.
 */
const NEAR_MISS_INTENTS: Record<string, string> = {
  standup: '/scene standup',
  governance: '/scene governance',
  planning: '/scene planning',
  quit: '/leave',
  exit: '/leave',
  status: '/do update-status',
  seats: '/view board',
  who: '/view board',
  commands: '/commands',
};

/**
 * "What can I do now" - a fold over the projection the client already holds (session-help.md §3.2).
 *
 * State-derived, not authored: no server call, no content to go stale, and every line carries the real
 * ids, because `/decide comms-posture ship` is a move and `/decide <id> <option>` is a grammar lesson
 * (standards/messages.md). A line renders only when its state exists - an empty board says so plainly
 * rather than listing moves that are not available yet.
 */
export function helpNow(app: AppApi): string[] {
  const id = app.activeSessionId();
  const view = id ? app.store.getSession(id) : undefined;
  const game = view?.game;
  const lines: string[] = [];

  if (!game) {
    // Chat-only session, or a game that has not sent its first delta yet.
    return [t('help.noBoard'), t('help.noBoard.action'), t('help.footer')];
  }

  const you = game.you;
  const seat = you ? game.seats.find((s) => s.key === you.seat) : undefined;

  lines.push(
    you
      ? t('help.you', {
          seat: you.seat,
          checkIn: you.checkIn,
          capacity: you.freeCapacity,
        })
      : t('help.noSeat'),
  );

  if (seat?.powers.length) {
    lines.push(t('help.verbs', { verbs: seat.powers.map((p) => `/do ${p}`).join(' · ') }));
  } else if (!you) {
    const free = game.seats.filter((s) => s.claimable).map((s) => s.key);
    lines.push(
      free.length
        ? t('help.takeSeat', { seats: free.map((k) => `/claim ${k}`).join(' · ') })
        : t('help.allSeatsTaken'),
    );
  }

  // The round: what closes your turn, or what has to happen before there is a turn to close.
  if (!game.started) {
    lines.push(t('help.notStarted'));
  } else if (you && you.checkIn === 'Pending') {
    lines.push(t('help.checkIn', { seat: you.seat }));
  }

  // The two things with a clock on them. Both name their ids and their options, because a player
  // reading this line should not have to go looking for either.
  const pending: string[] = [];
  if (game.activeEvent) {
    const options = game.activeEvent.options.map((o) => o.id).join('|');
    pending.push(
      t('help.pending.event', { id: game.activeEvent.id, options: options || '<option>' }),
    );
  }
  for (const d of game.decisions.filter((d) => d.mine).slice(0, 2)) {
    pending.push(
      t('help.pending.decision', { id: d.id, round: d.dueRound, options: d.options.join('|') }),
    );
  }
  if (pending.length) {
    lines.push(t('help.pending', { items: pending.join('   ·   ') }));
  }

  // The open scene's current beat. BEAT_PROMPTS is the client's mirror of the engine's beat→moves map
  // (session-help.md §3.2 records the drift risk as a decision); reused here rather than copied.
  if (game.scene) {
    const prompt = BEAT_PROMPTS[game.scene.beat];
    lines.push(
      t('help.scene', {
        kind: game.scene.kind,
        beat: game.scene.beat,
        prompt: prompt ? ` – ${t(prompt)}` : '',
      }),
    );
  } else if (game.started) {
    lines.push(t('help.scenes'));
  }

  const npc = game.seats.find((s) => s.occupant === 'npc');
  lines.push(t('help.talk', { seat: npc?.key ?? '<seat>' }));
  lines.push(t('help.footer'));
  return lines;
}

/** True when `a` and `b` are within one edit - a typo, not a different word. */
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;

  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < short.length && j < long.length) {
    if (short[i] === long[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    j++;
    if (short.length === long.length) i++;
  }
  return true;
}

/**
 * The unknown-command line, with the move it was probably reaching for. Exact-prefix first (a truncated
 * command), then one-edit (a typo), then the intent map (a domain word that is not a command).
 */
function nearMiss(cmd: string): string | null {
  const known = [...SESSION_COMMANDS.keys()];
  const prefix = known.find((k) => k.startsWith(cmd) && cmd.length > 1);
  if (prefix) return `/${prefix}`;

  const typo = known.find((k) => withinOneEdit(cmd, k));
  if (typo) return `/${typo}`;

  return NEAR_MISS_INTENTS[cmd] ?? null;
}

/**
 * The completion table for the session view, and the fold it reads (tab-completion.md §2.1-2.2).
 *
 * One substrate, three consumers: the same projection powers contextual `/help`, `/view guide` and this.
 * If the board ever grows buttons, they draw from the same table rather than a fourth copy of the rules.
 */
/**
 * Layer 1 + Layer 3 for this session (session-chrome.md §3.2, §3.4).
 *
 * The fourth consumer of the same fold: help answers "what now", the guide answers "what is this seat
 * for", completion answers "what can I type", and this answers "where am I". No new state, no wire
 * change - and because it is the same source, the breadcrumb cannot disagree with the board.
 */
export function sessionChrome(app: AppApi): TermChrome | null {
  const id = app.activeSessionId();
  if (!id) {
    return null;
  }

  const view = app.store.getSession(id);
  const game = view?.game;
  const you = game?.you;

  const where = [
    view?.meta.name ?? short(id),
    you ? t('chrome.you', { seat: you.seat, checkIn: you.checkIn }) : t('chrome.noSeat'),
    game ? t('chrome.clock', { round: game.round, phase: game.phase }) : '',
    game?.scene ? `${game.scene.kind}:${game.scene.beat}` : '',
    app.connectionStatus(),
  ].filter((part) => part.length > 0);

  return {
    breadcrumb: where.join('   ·   '),
    tabs: SESSION_PANELS.map((key) => {
      const size = sliceSize(view, key);
      // `all` stacks the board and the timeline, so being on it counts as looking at both.
      const looking = key === panel || (panel === 'all' && (key === 'board' || key === 'feed'));

      // Looking at it IS reading it, which is why this can be a render-time fold rather than an event
      // the panel switch has to remember to fire.
      if (size !== null && looking) {
        seen.set(key, size);
      }

      return {
        key,
        label: key,
        active: key === panel,
        unread: size !== null && size > (seen.get(key) ?? 0),
      };
    }),
    // The one next move (§3.4), in the order that decides what "most relevant" means.
    context: contextRow(app),
  };
}

/**
 * Layer 3's single line: the most relevant next move, permanently visible instead of on request.
 *
 * The fallback chain is the design's (§3.4) and its order is an argument: a pending event has the
 * shortest fuse, a due decision the next shortest, and your own check-in is the thing only you can do.
 * `/help` remains the full block - this is the one line worth taking a permanent row for.
 */
function contextRow(app: AppApi): string {
  const lines = helpNow(app);
  return (
    lines.find((line) => line.trimStart().startsWith('pending:')) ??
    lines.find((line) => line.trimStart().startsWith('this round:')) ??
    lines.find((line) => line.trimStart().startsWith('scene ')) ??
    ''
  ).trim();
}

/**
 * The session brief (VAL-3: an external group, no facilitator).
 *
 * Everything here is on the board somewhere already - the objective under `okrs:`, the clock in the
 * header, the seats in their block. What is missing without this is the SENTENCE that joins them: what
 * we are doing, by when, who is here, and what the first move is. A facilitator says that in ten
 * seconds; a group without one reads meters and guesses.
 */
export function briefLines(app: AppApi): string[] {
  const id = app.activeSessionId();
  const game = id ? app.store.getSession(id)?.game : null;
  if (!game) {
    return [t('help.noBoard'), t('help.noBoard.action')];
  }

  const lines = [t('brief.title')];

  // The clock is the pressure the whole scenario is built around, so it leads.
  lines.push(
    game.deadline === null
      ? t('brief.noDeadline', { round: game.round })
      : t('brief.deadline', { round: game.round, deadline: game.deadline }),
  );

  lines.push(
    game.okrs.objective
      ? t('brief.objective', { objective: game.okrs.objective })
      : t('brief.noObjective'),
  );

  // Who is actually in the room, and who is a bot - a group needs to know which silences are people.
  const humans = game.seats.filter((s) => s.occupant === 'human').map((s) => s.key);
  const bots = game.seats.filter((s) => s.occupant === 'npc').map((s) => s.key);
  lines.push(t('brief.who', { humans: humans.join(', ') || '-', bots: bots.join(', ') || '-' }));

  // How a session ENDS. This used to be the question a first-timer could not answer from the board at
  // all; since A1 the board carries the bars, so the brief states them rather than the shape of the rule.
  if (game.goal?.win.length) {
    lines.push(
      t('brief.win', {
        conditions: game.goal.win.map((w) => conditionText(w, false)).join(t('board.goalOr')),
      }),
    );
  }

  if (game.goal?.lose.length) {
    lines.push(
      t('brief.lose', {
        conditions: game.goal.lose.map((l) => conditionText(l, true)).join(' · '),
      }),
    );
  }

  lines.push(t('brief.ends'));

  // …and the one move that starts it, so the brief hands off to the game rather than to another
  // question. Reusing the help fold's first line keeps the two from disagreeing.
  const next = helpNow(app).find((line) => line.trimStart().startsWith('this round:'));
  if (next) {
    lines.push(next.trim());
  }

  lines.push(t('brief.footer'));
  return lines;
}

export function sessionCompletion(app: AppApi, text: string, caret: number): Completion {
  const id = app.activeSessionId();
  return complete({
    commands: SESSION_COMMANDS,
    fold: {
      game: (id ? app.store.getSession(id)?.game : null) ?? null,
      panels: SESSION_PANELS,
      sessions: [],
      // A session view has no lobby catalog to complete from; `create` lives in the lobby.
      decks: [],
      pending: id
        ? app
            .outbox()
            .pendingIn(id)
            .lines.filter((l) => l.state === 'failed')
            .map((l) => short(l.cmdId))
        : [],
    },
    text,
    caret,
  });
}

export function sessionCommand(app: AppApi, raw: string): void {
  const text = raw.trim();
  if (!text) return;
  // `/help` answers "what now"; the catalog answers "what exists" and moved to `/commands`. The question
  // players actually type is the first one, so it gets the front door (session-help.md §1).
  if (text === '/help' || text === '/?' || HELP_PLEAS.has(text.toLowerCase())) {
    for (const line of helpNow(app)) {
      app.print(line);
    }

    return;
  }
  if (text === '/commands') {
    for (const line of commandCatalog()) {
      app.print(line);
    }

    return;
  }
  if (text === '/leave') {
    void app.leaveSession();
    return;
  }
  if (text.startsWith('/ask ')) {
    // `/ask <seat> <topic-or-question>`. A closed key goes straight through; anything else is a
    // question in the player's own words, resolved server-side to one of those keys before any NPC
    // prompt exists (S2.4 §5). The client does not guess which is which - it only notices the shape.
    const [, seat, ...rest] = text.split(/\s+/);
    const asked = rest.join(' ').trim();
    if (!seat || !asked) {
      app.print(t('msg.askUsage'));
      return;
    }

    const closed = asked === 'mood' || /^(decision|concern):\S+$/.test(asked);
    void app.askNpc(seat, closed ? asked : '', closed ? undefined : asked);
    return;
  }

  // `/okr <goal>` - free text in, gradable key results out (S2.4 §3.3). Handled here rather than in
  // gameInput() because it sends no game action at all: the reply is a suggestion the player adopts by
  // typing the /kr it names.
  if (text === '/okr' || text.startsWith('/okr ')) {
    const goal = text.slice('/okr'.length).trim();
    if (!goal) {
      app.print(t('okr.usage'));
      return;
    }

    void app.compileOkr(goal);
    return;
  }
  // `/policy <agreement>` drafts, `/adopt <id>` commits. Two commands rather than one on purpose: the
  // table reads what a rule does - both halves - before it is anyone's rule (S2.4 §4.2 step 4).
  if (text === '/policy' || text.startsWith('/policy ')) {
    const draft = text.slice('/policy'.length).trim();
    if (!draft) {
      app.print(t('policy.usage'));
      return;
    }

    void app.draftPolicy(draft);
    return;
  }
  if (text === '/adopt' || text.startsWith('/adopt ')) {
    const id = text.slice('/adopt'.length).trim();
    if (!id) {
      app.print(t('policy.adoptUsage'));
      return;
    }

    void app.adoptPolicy(id);
    return;
  }
  // `/brief` - what this session IS (VAL-3). Contextual /help answers "what do I type next"; nobody
  // was answering "what is this and what counts as winning", which a group with no facilitator has to
  // work out from meters and seat names. Same fold, one more question.
  if (text === '/brief') {
    for (const line of briefLines(app)) {
      app.print(line);
    }

    return;
  }
  if (text === '/retro') {
    void app.showRetro();
    return;
  }
  // The outbox's two commands. `/retry` sends a refused line again from the text it kept; `/discard`
  // takes it off the scrollback. Both take the short id printed beside the line.
  if (text.startsWith('/retry') || text.startsWith('/discard')) {
    const [verb, handle] = text.split(/\s+/);
    const sid = app.activeSessionId();
    const waiting = sid
      ? app
          .outbox()
          .pendingIn(sid)
          .lines.filter((l) => l.state === 'failed')
      : [];

    if (!handle) {
      // A problem message names the action: without the ids there is nothing to type.
      app.print(
        waiting.length
          ? t('chat.pendingList', { ids: waiting.map((l) => short(l.cmdId)).join(', ') })
          : t('chat.nonePending'),
      );
      return;
    }

    const line = waiting.find((l) => short(l.cmdId) === handle);
    if (!line) {
      app.print(t('app.noSuchPending', { id: handle }));
      return;
    }

    if (verb === '/retry') void app.retrySend(line.cmdId);
    else app.discardSend(line.cmdId);
    return;
  }
  if (text === '/milestones') {
    // Read from the store rather than fetched: chapters commit as events and replay on resume, so the
    // client that is connected already has them and one that reconnected has just been given them.
    const sid = app.activeSessionId();
    const chapters = sid ? (app.store.getSession(sid)?.milestones ?? []) : [];
    for (const line of renderMilestones(chapters)) app.print(line.text);
    return;
  }
  // Panel switch: `/view board|feed|me|all` (the first step toward the 7-view tui-layout).
  if (text === '/view' || text.startsWith('/view ')) {
    const want = text.slice(1).split(/\s+/)[1];
    if (want && (SESSION_PANELS as readonly string[]).includes(want)) {
      setSessionPanel(want as SessionPanel);
      app.print(t('msg.panelSwitched', { panel: want }));
    } else {
      app.print(t('msg.viewUsage', { panels: SESSION_PANELS.join('|'), panel }));
    }
    return;
  }
  // `/do <verb> [seat]` - the seat's own moves (session-help.md §3.1). Handled here rather than in
  // gameInput() because the seat defaults to the one you hold, which only the projection knows.
  //
  // This is the command the board has been promising since S1: every `can:` line named verbs the engine
  // has always accepted and no client could send. A capability nothing can reach is not a capability.
  if (text === '/do' || text.startsWith('/do ')) {
    const [, verb, named] = text.split(/\s+/, 3);
    const sid = app.activeSessionId();
    const game = sid ? app.store.getSession(sid)?.game : undefined;
    const seat = named ?? game?.you?.seat;

    if (!verb) {
      // Name the verbs THIS player can actually send, not the shape of the command.
      const mine = game?.seats.find((s) => s.mine);
      app.print(
        mine?.powers.length
          ? t('msg.doUsage', { verbs: mine.powers.map((p) => `/do ${p}`).join(' · ') })
          : t('msg.doNoSeat'),
      );
      return;
    }
    if (!seat) {
      app.print(t('msg.doNeedsSeat', { verb }));
      return;
    }

    // The engine owns whether this seat may do this now (PowerDenied, timing) and its refusals already
    // name the seat and the window, so the client does not second-guess it here.
    void app.sendGameAction({ kind: 'seatAction', seat, verb });
    return;
  }
  // Resolve the director's pending event: `/resolve [optionId]`. The event id comes from the projected
  // board (the client does not carry it on the wire), so this is handled here rather than in gameInput().
  if (text === '/resolve' || text.startsWith('/resolve ')) {
    const sid = app.activeSessionId();
    const active = sid ? app.store.getSession(sid)?.game?.activeEvent : undefined;
    if (!active) {
      app.print(t('msg.noPendingEvent'));
      return;
    }
    const optionId = text.slice(1).split(/\s+/)[1];
    void app.sendGameAction({
      kind: 'resolveEvent',
      eventId: active.id,
      ...(optionId ? { optionId } : {}),
    });
    return;
  }
  if (text.startsWith('/')) {
    const [cmd, ...rest] = text.slice(1).split(/\s+/);
    const input = gameInput(cmd ?? '', rest);
    if (input) {
      void app.sendGameAction(input);
      return;
    }
    // A command that EXISTS but was given the wrong arguments gets its own usage line; only a name
    // nobody knows is "unknown". Telling someone /decide does not exist, when the problem is that they
    // typed it without arguments, sends them looking for the wrong thing.
    const usage = SESSION_COMMANDS.get(cmd ?? '')?.usage;
    if (usage) {
      app.print(t('msg.usage', { usage }));
      return;
    }

    // A near miss is a player who guessed the vocabulary right (session-help.md §3.3). Naming the real
    // move beats telling them they know nothing, which is what "unknown command" says.
    const meant = nearMiss((cmd ?? '').toLowerCase());
    app.print(
      meant ? t('msg.nearMiss', { cmd: cmd ?? '', meant }) : t('msg.unknownCommand', { text }),
    );
    return;
  }
  const at = mentionOf(text);
  if (at) {
    const sid = app.activeSessionId();
    const seats = (sid ? app.store.getSession(sid)?.game?.seats : undefined) ?? [];
    // Seats exist only once a scenario runs; until then a session is chat-only and `@anything` is just
    // text. Check the name when there is a roster to check it against, and stay out of the way before.
    if (seats.length && !seats.some((s) => s.key === at.seat)) {
      app.print(t('msg.noSeatNamed', { seat: at.seat, seats: seats.map((s) => s.key).join(', ') }));
      return;
    }
    if (!at.rest) {
      app.print(t('msg.mentionUsage', { usage: MENTION_USAGE }));
      return;
    }

    // Addressing an NPC still goes to the room - that is what @ means - but the room is the wrong place
    // to wait for an answer that cannot come yet (session-help.md §3.3). Say so locally rather than
    // letting the silence read as the NPC ignoring them.
    const addressed = seats.find((s) => s.key === at.seat);
    if (addressed?.occupant === 'npc') {
      app.print(t('msg.npcMention', { seat: at.seat }));
    }
  }
  void app.sendChat(text);
}

/** Maps a slash command to a gameplay input for the S1 bridge, or null when it isn't one. */
function gameInput(cmd: string, rest: string[]): GameActionPayload | null {
  switch (cmd) {
    case 'start':
      return { kind: 'startSession' };
    case 'beat':
      return { kind: 'advanceBeat' };
    case 'claim':
      // The server stamps the player id from the connection; the wire carries only the seat.
      return rest.length >= 1 ? { kind: 'claimSeat', seat: rest[0] } : null;
    case 'release':
      // Which seat is not on the wire either: you can only ever give up your own.
      return { kind: 'releaseSeat' };
    case 'mood':
      return rest.length >= 2 ? { kind: 'moodPulse', seat: rest[0], mood: rest[1] } : null;
    case 'scene':
      return rest.length >= 1 ? { kind: 'openScene', scene: rest[0] } : null;
    case 'checkin':
      return rest.length >= 1
        ? { kind: 'checkIn', seat: rest[0], mode: rest[1] ?? 'Passed' }
        : null;
    // WIP / backlog (§2.8).
    case 'add': {
      if (rest.length < 3) return null;
      const effort = Number(rest[2]);
      return Number.isFinite(effort)
        ? { kind: 'addBacklogItem', id: rest[0], type: rest[1], effort }
        : null;
    }
    case 'work':
      return rest.length >= 2 ? { kind: 'startWork', item: rest[0], seat: rest[1] } : null;
    // DEC-17 M3: how much capacity this item holds, and so how fast it moves.
    case 'allocate': {
      if (rest.length < 2) return null;
      const units = Number(rest[1]);
      return Number.isFinite(units) ? { kind: 'allocateWork', item: rest[0], units } : null;
    }
    case 'pause':
      return rest.length >= 1 ? { kind: 'pauseWork', item: rest[0] } : null;
    case 'abandon':
      return rest.length >= 1 ? { kind: 'abandonWork', item: rest[0] } : null;
    // OKRs (§7.5). `/kr <id> <expr...>` — the expression is both the target and the label.
    case 'objective':
      return rest.length >= 1 ? { kind: 'setObjective', text: rest.join(' ') } : null;
    case 'kr': {
      if (rest.length < 2) return null;
      const expr = rest.slice(1).join(' ');
      return { kind: 'addKeyResult', id: rest[0], description: expr, target: expr };
    }
    case 'rmkr':
      return rest.length >= 1 ? { kind: 'removeKeyResult', krId: rest[0] } : null;
    // The standup's share slot (§6.5 beat 2) and the governance docket (§7.6) — the other two families
    // the scene machine gates by beat.
    case 'share':
      return rest.length >= 2 ? { kind: 'shareItem', seat: rest[0], itemId: rest[1] } : null;
    // A promise to the outside world (§7.5b). `<what>` is free text and `by <round>` closes it, so the
    // content can contain spaces without the parser guessing where it ends - the last "by N" wins,
    // which is what a person typing "ship the SLA dashboard by 4" means.
    case 'promise': {
      if (rest.length < 3) return null;
      const at = rest.lastIndexOf('by');
      if (at < 2 || at === rest.length - 1) return null;
      const dueRound = Number(rest[at + 1]);
      if (!Number.isInteger(dueRound)) return null;
      return {
        kind: 'makePromise',
        stakeholder: rest[0],
        content: rest.slice(1, at).join(' '),
        dueRound,
      };
    }
    case 'kept':
      return rest.length >= 1 ? { kind: 'keepPromise', promiseId: rest[0] } : null;
    case 'broke':
      return rest.length >= 1 ? { kind: 'breakPromise', promiseId: rest[0] } : null;
    case 'concern':
      return rest.length >= 2
        ? { kind: 'raiseConcern', seat: rest[0], topic: rest.slice(1).join(' ') }
        : null;
    case 'resolveconcern':
      return rest.length >= 1 ? { kind: 'resolveConcern', concernId: rest[0] } : null;
    // The decision queue (§6.5/§7.4). `/raise` opens an AUTHORED call by id (the wire never carries
    // effects); the rest are the moves each rights style needs before the call can close.
    case 'raise':
      return rest.length >= 1
        ? { kind: 'raiseDecision', decisionId: rest[0], ...(rest[1] ? { owner: rest[1] } : {}) }
        : null;
    case 'decide':
      return rest.length >= 2
        ? {
            kind: 'resolveDecision',
            decision: rest[0],
            option: rest[1],
            ...(rest[2] ? { bySeat: rest[2] } : {}),
          }
        : null;
    case 'defer':
      return rest.length >= 1 ? { kind: 'deferDecision', decision: rest[0] } : null;
    case 'consult':
      return rest.length >= 2
        ? { kind: 'consultDecision', decision: rest[0], seat: rest[1] }
        : null;
    case 'vote':
      return rest.length >= 3
        ? { kind: 'voteDecision', decision: rest[0], seat: rest[1], option: rest[2] }
        : null;
    case 'approve':
      return rest.length >= 2
        ? { kind: 'approveDecision', decision: rest[0], seat: rest[1] }
        : null;
    // The policy ledger (§7.6). Adoption happens by resolving a motion; what a player drives directly is
    // the structured after-work: move an active policy onto an authored variant, or strike it.
    case 'amend':
      return rest.length >= 3
        ? { kind: 'amendPolicy', policyId: rest[0], amendmentId: rest[1], seat: rest[2] }
        : null;
    case 'repeal':
      return rest.length >= 2 ? { kind: 'repealPolicy', policyId: rest[0], seat: rest[1] } : null;
    default:
      return null;
  }
}
